import { Fragment, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { ReportQuestionRow } from '../../../api/types';
import { Badge, Checkbox, ShowMoreFooter, SortableTh, Table, TEXT_LINK, Term } from '../../../components/ui';
import { Link } from 'react-router-dom';
import { useSortableTable, SortColumn } from '../../../lib/useSortableTable';
import { TH_RULE } from '../../../lib/tableRules';

const REASON_LABEL: Record<string, string> = {
  negative_discrimination: 'Negative discrimination: strong students got it wrong more than weak students. Check the key.',
  flat_distractors: 'Flat distractors: every option picked about equally. The question may be ambiguous.',
  key_minority: 'Key minority: the marked answer was not the most-picked option. Check the key.',
};

const REPORT_QUESTIONS_COLUMNS: SortColumn<ReportQuestionRow>[] = [
  { key: 'order', accessor: (q) => q.order, type: 'number' },
  { key: 'question', accessor: (q) => q.excerpt },
  { key: 'chapter', accessor: (q) => q.chapter_name },
  { key: 'pct_correct', accessor: (q) => q.pct_correct, type: 'number' },
  { key: 'discrimination', accessor: (q) => q.discrimination, type: 'number' },
  { key: 'avg_time', accessor: (q) => q.avg_time_sec, type: 'number' },
  { key: 'quality', accessor: (q) => q.quality_score, type: 'number' },
  { key: 'suspect', accessor: (q) => (q.suspect ? 1 : 0), type: 'number' },
];

/** "Average 2.0/4 · 1 to mark" for a written question's % correct cell. */
function writtenSummary(q: ReportQuestionRow): string {
  const avg = q.avg_marks == null ? '\u2014' : q.avg_marks.toFixed(1);
  const base = `Average ${avg}/${q.max_marks ?? '\u2014'}`;
  return q.to_mark ? `${base} \u00b7 ${q.to_mark} to mark` : base;
}

const PREVIEW_ROWS = 10;
const CELL = 'border-b border-hairline px-2.5 py-3 align-middle';
const NUM = `${CELL} whitespace-nowrap text-right font-mono font-medium`;

interface Props {
  rows: ReportQuestionRow[];
  /** Controlled suspect filter (the page sets it from "Review suspect questions"); local state when omitted. */
  suspectOnly?: boolean;
  onSuspectOnlyChange?: (v: boolean) => void;
  /** When set, a suspect question's drawer links back to this exam's answer key. */
  examId?: number;
}

export default function ReportQuestionsTable({ rows: inputRows, suspectOnly: suspectProp, onSuspectOnlyChange, examId }: Props) {
  const [open, setOpen] = useState<number | null>(null);
  const [localSuspect, setLocalSuspect] = useState(false);
  const suspectOnly = suspectProp ?? localSuspect;
  const setSuspectOnly = onSuspectOnlyChange ?? setLocalSuspect;
  const { rows, sort, toggle } = useSortableTable(inputRows, REPORT_QUESTIONS_COLUMNS, { key: 'order', dir: 'asc' });
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { setShowAll(false); }, [suspectOnly]);
  const filtered = suspectOnly ? rows.filter((q) => q.suspect) : rows;
  const visible = showAll ? filtered : filtered.slice(0, PREVIEW_ROWS);
  const suspectCount = inputRows.filter((q) => q.suspect).length;
  const fmt = (v: number | null, suffix = '') => (v == null ? '—' : `${v.toFixed(1)}${suffix}`);
  const fmtDisc = (v: number | null) => (v == null ? '—' : v.toFixed(2));
  const fmt2 = (v: number | null) => (v == null ? '—' : v.toFixed(2));
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[13px] text-muted">Click a question to see how the options were picked.</p>
        <label className="flex min-h-[44px] items-center gap-2.5 text-sm text-ink">
          <Checkbox checked={suspectOnly} onChange={(e) => setSuspectOnly(e.target.checked)} />
          Suspect only ({suspectCount})
        </label>
      </div>
      <Table minWidth={900}>
        <thead>
          <tr>
            <SortableTh className={TH_RULE} columnKey="order" sort={sort} onToggle={toggle}>#</SortableTh>
            <SortableTh className={`${TH_RULE} min-w-[280px]`} columnKey="question" sort={sort} onToggle={toggle}>Question</SortableTh>
            <SortableTh className={TH_RULE} columnKey="chapter" sort={sort} onToggle={toggle}>Chapter</SortableTh>
            <SortableTh className={TH_RULE} columnKey="pct_correct" sort={sort} onToggle={toggle} align="right" help="pct_correct">% correct</SortableTh>
            <SortableTh className={TH_RULE} columnKey="discrimination" sort={sort} onToggle={toggle} align="right" help="discrimination">Discrimination</SortableTh>
            <SortableTh className={TH_RULE} columnKey="avg_time" sort={sort} onToggle={toggle} align="right" help="question_avg_time">Avg time</SortableTh>
            <SortableTh className={TH_RULE} columnKey="quality" sort={sort} onToggle={toggle} align="right" help="quality">Quality</SortableTh>
            {/* No visible label (matches the prior plain header) — the body's "Suspect"
                badge is the only place that word should appear on the page. */}
            <SortableTh className={TH_RULE} columnKey="suspect" sort={sort} onToggle={toggle}>{null}</SortableTh>
          </tr>
        </thead>
        <tbody>
          {visible.map((q) => {
            const isOpen = open === q.question_id;
            const toggleOpen = () => setOpen(isOpen ? null : q.question_id);
            const Chev = isOpen ? ChevronDown : ChevronRight;
            const total = q.options.reduce((n, o) => n + o.count, 0);
            return (
            <Fragment key={q.question_id}>
              <tr className="group cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={toggleOpen}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleOpen();
                    }
                  }}>
                <td className={`${CELL} font-mono font-medium text-muted`}>{q.order}</td>
                <td className={`${CELL} font-medium text-ink`}>
                  <span className="flex items-center gap-2 group-hover:underline group-hover:underline-offset-[3px]">
                    <Chev size={14} strokeWidth={1.8} aria-hidden="true" className="flex-none text-muted" />{q.excerpt}
                  </span>
                </td>
                <td className={`${CELL} text-[13px] text-muted`}>{q.chapter_name ?? '\u2014'}</td>
                <td className={`${NUM}`}>
                  {q.type === 'written' ? (
                    <span className="font-sans text-xs font-semibold text-ink">{writtenSummary(q)}</span>
                  ) : q.pct_correct == null ? '\u2014' : q.pct_correct < 50 ? (
                    <Badge tone="accent" className="font-sans">{fmt(q.pct_correct, '%')}</Badge>
                  ) : fmt(q.pct_correct, '%')}
                </td>
                <td className={`${NUM} ${q.discrimination != null && q.discrimination < 0 ? 'text-danger-ink' : ''}`}>{fmtDisc(q.discrimination)}</td>
                <td className={`${NUM} text-muted`}>{q.avg_time_sec == null ? '\u2014' : `${Math.round(q.avg_time_sec)}s`}</td>
                <td className={`${NUM}`}>{q.quality_score ?? '\u2014'}</td>
                <td className={CELL}>{q.suspect && <Term k="suspect" plain><Badge tone="danger">Suspect</Badge></Term>}</td>
              </tr>
              {isOpen && (
                <tr>
                  <td className="bg-surface-2" />
                  <td colSpan={7} className="bg-surface-2 px-2.5 pb-[18px] pt-3.5">
                    <div className="grid gap-[22px] md:grid-cols-[7fr_5fr]">
                      <div>
                        {q.type === 'written' || q.graded_by_teacher ? (
                          <p className="text-xs text-muted">Written answer — marked by the teacher</p>
                        ) : q.options.map((o) => {
                          const share = total ? (o.count / total) * 100 : 0;
                          return (
                            <div key={o.label} className="flex items-center gap-2.5 py-[5px] text-[13px]">
                              <span className={`w-7 flex-none font-mono font-medium ${o.is_correct ? 'text-success' : 'text-muted'}`}>{o.label}{o.is_correct ? ' \u2713' : ''}</span>
                              <span className="min-w-0 flex-[0_1_160px] truncate">{o.text}</span>
                              <span className="flex-1">
                                <span role="img" aria-label={`Option ${o.label} share`} className={`block h-2 rounded ${o.is_correct ? 'bg-slate-ink' : 'bg-line-hover'}`} style={{ width: `${Math.max(2, Math.round(share))}%` }} />
                              </span>
                              <span className="w-7 text-right font-mono font-medium">{o.count}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="text-[13px] text-muted-2">
                        <p className="mb-2"><strong className="text-ink"><Term k="difficulty_pred_obs">Difficulty</Term>:</strong> predicted {fmt2(q.predicted_difficulty)} → observed {fmt2(q.observed_difficulty)}</p>
                        {q.suspect_reasons.map((r) => (
                          <p key={r} className="mb-2 rounded-[10px] bg-danger-bg px-2.5 py-2 text-danger-ink">{REASON_LABEL[r] ?? r}</p>
                        ))}
                        {q.flags.length > 0 && <p className="mb-2">Generator flags: {q.flags.join(', ')}</p>}
                        {q.suspect && examId != null && (
                          <Link to={`/exams/exam-detail/${examId}`} className={TEXT_LINK}>Check the answer key in the exam {'\u2192'}</Link>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
            );
          })}
          {visible.length === 0 && (
            <tr>
              <td colSpan={8} className="px-3 py-6 text-center text-sm text-muted">No suspect questions.</td>
            </tr>
          )}
        </tbody>
      </Table>
      <ShowMoreFooter variant="button" showAllLabel={`Show all ${filtered.length} questions`} shown={visible.length} total={filtered.length} expanded={showAll} onToggle={() => setShowAll((v) => !v)} />
    </div>
  );
}
